import { useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import SVG from 'react-inlinesvg';
import Input from 'lib/Input';
import searchSvg from 'assets/icons/search.svg';
import { Store } from 'components/common/types/Store.types';
import { InputType } from 'components/common/types/Input.types';
import InputNumber from 'lib/InputNumber';
import { PoiOption } from 'components/common/types/Planner.types';
import { POI_DEFAULT_RADIUS, POI_MAX_RADIUS, POI_MIN_RADIUS } from '../../PointsOfInterest/consts';
import { parseLocaleNumber } from './utils';
import { OPEN_STREET_MAP_DATA_PROVIDER } from './consts';

type OpenStreetMapPoiSearchProps = {
  onIncludeExcludeChange: (option: PoiOption, optionIndex: number, selectedProvider: string) => void;
  onChangeRadius: (radiusValue: number, option: PoiOption, optionIndex: number) => void;
};

const RADIUS_CANT_BE_0_ERROR_MESSAGE = 'Radius must be greater than 0. Please enter a larger value.';

export const OpenStreetMapPoiSearch: React.FC<OpenStreetMapPoiSearchProps> = ({
  onIncludeExcludeChange,
  onChangeRadius,
}) => {
  const selectedOpenStreetMapPoi = useSelector(
    (state: Store) => state.dealManagement.commonDeal.currentLine.proximity.openStreetMapPoi,
  )[0];

  const [searchText, setSearchText] = useState(selectedOpenStreetMapPoi?.name);
  const [radius, setRadius] = useState(selectedOpenStreetMapPoi?.radius ?? POI_DEFAULT_RADIUS);

  const currentOpenStreetMapPoiOption: PoiOption = useMemo(() => {
    return {
      ...selectedOpenStreetMapPoi,
      selectable: true,
      code: searchText,
      name: searchText,
      categoryType: searchText,
      radius,
      include: true,
    };
  }, [selectedOpenStreetMapPoi, searchText, radius]);

  const hasValidSearchText = (): boolean => {
    return Boolean(currentOpenStreetMapPoiOption?.code && searchText);
  };

  const onSearchTextFocus = (): void => {
    if (!hasValidSearchText()) return;

    const previousSelectedOpenStreetMapPoiOption: PoiOption = {
      ...currentOpenStreetMapPoiOption,
      include: undefined,
    };

    onIncludeExcludeChange(previousSelectedOpenStreetMapPoiOption, 0, OPEN_STREET_MAP_DATA_PROVIDER.value);
  };

  const onSearchTextChange = (currentSearchText: string): void => {
    setSearchText(currentSearchText);
  };

  const onSearchTextBlur = (): void => {
    if (!hasValidSearchText()) return;

    onIncludeExcludeChange(currentOpenStreetMapPoiOption, 0, OPEN_STREET_MAP_DATA_PROVIDER.value);
  };

  const onRadiusChange = (currentRadius: number): void => {
    setRadius(currentRadius);
  };

  const onRadiusBlur = (currentRadius: string): void => {
    const sanitizedRadius = parseLocaleNumber(currentRadius);

    onChangeRadius(sanitizedRadius, currentOpenStreetMapPoiOption, 0);
  };

  return (
    <div className="grid grid-cols-[1fr_110px] gap-2">
      <Input
        label="POI category"
        name="open-street-map-poi-search"
        type={InputType.SEARCH}
        placeholder="Search POI category"
        endComponent={<SVG src={searchSvg} className="-ml-2.5 mr-0.5" />}
        value={searchText}
        onFocus={onSearchTextFocus}
        onChange={onSearchTextChange}
        onBlur={onSearchTextBlur}
      />
      <InputNumber
        label="Radius"
        name="open-street-map-poi-radius"
        min={POI_MIN_RADIUS}
        max={POI_MAX_RADIUS}
        maximumFractionDigits={0}
        endComponent={<span>m</span>}
        isDisabled={!searchText}
        value={radius}
        onChange={onRadiusChange}
        onBlur={onRadiusBlur}
        errorMessage={radius === 0 ? RADIUS_CANT_BE_0_ERROR_MESSAGE : undefined}
      />
    </div>
  );
};
